import { create } from 'zustand';
import type { Participant } from '../interfaces/Participant';

export interface BoardSessionState {
  boardId: string | null;
  authToken: string | null;
  key: string | null;
  isGuest: boolean;
  user: Participant | null;
}

export interface UserState {
  boardSession: BoardSessionState;
  getCurrentUser: () => Participant | null;
  getBoardSession: (boardId?: string) => BoardSessionState;
  setCurrentUser: (user: Participant | null, options?: { isGuest?: boolean }) => void;
  setBoardSession: (
    boardId: string,
    options?: {
      authToken?: string;
      key?: string | null;
      isGuest?: boolean;
    }
  ) => void;
  setBoardSessionKey: (key: string | null) => void;
  clearAllBoardSessions: () => void;
}

const TOPIC_USER_STORAGE_KEY_PREFIX = 'dotstorming_topic_user';
const KEY_STORAGE_KEY_PREFIX = 'dotvoting.boardSession.key';

const getTopicUserStorageKey = (boardId: string) => `${TOPIC_USER_STORAGE_KEY_PREFIX}_${boardId}`;
const getKeyStorageKey = (boardId: string) => `${KEY_STORAGE_KEY_PREFIX}.${boardId}`;

const readStoredUser = (boardId: string | null): Participant | null => {
  if (typeof window === 'undefined' || !boardId) {
    return null;
  }

  try {
    const storedValue = window.localStorage.getItem(getTopicUserStorageKey(boardId));
    if (!storedValue?.trim()) {
      return null;
    }

    const parsed = JSON.parse(storedValue) as {
      id?: unknown;
      name?: unknown;
      votes?: unknown;
    };
    const id = typeof parsed.id === 'string' ? parsed.id.trim() : '';
    const name = typeof parsed.name === 'string' ? parsed.name.trim() : '';
    const votes = typeof parsed.votes === 'number' ? parsed.votes : 0;

    if (!id || !name) {
      return null;
    }

    return { id, name, votes };
  } catch {
    return null;
  }
};

const readStoredKey = (boardId: string | null): string | null => {
  if (typeof window === 'undefined' || !boardId) {
    return null;
  }

  try {
    const storedValue = window.localStorage.getItem(getKeyStorageKey(boardId));
    return storedValue?.trim() ? storedValue : null;
  } catch {
    return null;
  }
};

const persistBoardSession = (session: BoardSessionState) => {
  if (typeof window === 'undefined' || !session.boardId) {
    return;
  }

  try {
    const userKey = getTopicUserStorageKey(session.boardId);
    const keyKey = getKeyStorageKey(session.boardId);

    if (session.user && session.isGuest) {
      window.localStorage.setItem(userKey, JSON.stringify(session.user));
    } else {
      window.localStorage.removeItem(userKey);
    }

    if (session.key) {
      window.localStorage.setItem(keyKey, session.key);
    } else {
      window.localStorage.removeItem(keyKey);
    }
  } catch {
    // Ignore storage errors (private mode, quotas, etc.) and keep in-memory state.
  }
};

const refreshStoredSession = (
  session: BoardSessionState,
  boardId = session.boardId
): BoardSessionState => {
  if (!boardId) {
    return { ...session };
  }

  const nextUser = session.authToken ? session.user : readStoredUser(boardId);

  return {
    ...session,
    boardId,
    user: nextUser,
    key: readStoredKey(boardId),
  };
};

const useUserStore = create<UserState>((set, get) => ({
  boardSession: {
    boardId: null,
    authToken: null,
    key: null,
    isGuest: false,
    user: null,
  },
  getCurrentUser: () => get().boardSession.user,
  getBoardSession: (boardId) => refreshStoredSession(get().boardSession, boardId),
  setCurrentUser: (user, options) => {
    const nextSession = {
      ...get().boardSession,
      user,
      isGuest: options?.isGuest ?? (user ? get().boardSession.isGuest : false),
    };

    persistBoardSession(nextSession);
    set({ boardSession: nextSession });
  },
  setBoardSession: (boardId, options) => {
    const currentSession = get().boardSession;
    const nextAuthToken = options?.authToken ?? null;
    const authChanged = currentSession.authToken !== nextAuthToken;
    const boardChanged = currentSession.boardId !== boardId;
    const key = options?.key?.trim();

    const nextSession = refreshStoredSession({
      ...currentSession,
      boardId,
      authToken: nextAuthToken,
      user:
        boardChanged || authChanged
          ? nextAuthToken
            ? currentSession.user
            : null
          : currentSession.user,
      isGuest: options?.isGuest ?? Boolean(currentSession.user && !nextAuthToken),
    });

    nextSession.isGuest = options?.isGuest ?? Boolean(nextSession.user && !nextAuthToken);

    if (key) {
      nextSession.key = key;
    }

    persistBoardSession(nextSession);
    set({ boardSession: nextSession });
  },
  setBoardSessionKey: (key) => {
    const nextSession = {
      ...get().boardSession,
      key: key?.trim() || null,
    };

    persistBoardSession(nextSession);
    set({ boardSession: nextSession });
  },
  clearAllBoardSessions: () => {
    if (typeof window !== 'undefined') {
      try {
        const keysToRemove: string[] = [];
        for (let index = 0; index < window.localStorage.length; index += 1) {
          const key = window.localStorage.key(index);
          if (
            key?.startsWith(`${TOPIC_USER_STORAGE_KEY_PREFIX}_`) ||
            key?.startsWith(`${KEY_STORAGE_KEY_PREFIX}.`)
          ) {
            keysToRemove.push(key);
          }
        }

        keysToRemove.forEach((key) => {
          window.localStorage.removeItem(key);
        });
      } catch {
        // Ignore storage errors and keep state cleared in memory.
      }
    }

    set({
      boardSession: {
        boardId: null,
        authToken: null,
        key: null,
        isGuest: false,
        user: null,
      },
    });
  },
}));

export default useUserStore;
